// STEP 3-SHIP-B · Carrier Excel Profile V1
// ─────────────────────────────────────────────────────────────────────────
// Profiles are pure mappers: LIVEMOA canonical PhysicalShipment → carrier rows.
// Adding a new carrier = adding one entry here (no OMS/WMS/Combined engine
// changes). Headers/columns are transcribed VERBATIM from the sample CSVs
// provided by the user (CJ대한통운.csv · 한진택배.csv · 롯데택배.csv).
// Encoding: UTF-8 with BOM (matches all 3 samples).
//
// Canonical PhysicalShipment shape:
//   {
//     orderNo,              // 대표 주문번호 (합포: 첫 member order_number)
//     combinedShipmentId,   // null | cshp_...
//     recipient,            // {name, phone, altPhone?, postalCode, address}
//     items: [{name, option, quantity}],
//     deliveryMemo,
//   }
// ─────────────────────────────────────────────────────────────────────────

/** Aggregate items into a single carrier-safe string (name×qty · options preserved). */
const _joinItems = (items) => {
  return (items || []).map((it) => {
    const opt = it.option && Object.keys(it.option).length
      ? ' (' + Object.entries(it.option).map(([k, v]) => `${k}=${v}`).join(', ') + ')'
      : '';
    return `${it.name || ''}${opt} × ${it.quantity || 1}`;
  }).join(' + ');
};

const _sumQty = (items) => (items || []).reduce((s, it) => s + (parseInt(it.quantity, 10) || 1), 0) || 1;

// ─── Profile registry ────────────────────────────────────────────────────
export const CARRIER_PROFILES = {
  STANDARD_LIVEMOA: {
    id: 'STANDARD_LIVEMOA',
    label: 'LIVEMOA 표준양식',
    fileExt: 'xlsx',
    encoding: 'utf-8',
    status: 'available',
    // Backwards-compatible with existing SimpleOrders export
    headers: ['주문번호', 'seller_order_id', '합포그룹', '수취인', '전화', '우편번호',
               '주소', '배송메모', '상품명', '옵션', '수량', '택배사', '송장번호'],
    // rowsPerShipment: 1 row per item (preserves per-item detail)
    build: (ps) => (ps.items || [{}]).map((it) => ({
      '주문번호': ps.orderNo || '',
      'seller_order_id': ps.combinedShipmentId ? '' : (ps.sellerOrderId || ''),
      '합포그룹': ps.combinedShipmentId || '',
      '수취인': ps.recipient?.name || '',
      '전화': ps.recipient?.phone || '',
      '우편번호': ps.recipient?.postalCode || '',
      '주소': ps.recipient?.address || '',
      '배송메모': ps.deliveryMemo || '',
      '상품명': it.name || '',
      '옵션': it.option ? Object.entries(it.option).map(([k, v]) => `${k}=${v}`).join(', ') : '',
      '수량': it.quantity || 1,
      '택배사': '',
      '송장번호': '',
    })),
  },

  CJ: {
    // A4-2A · Real Verified via `CJ양식 1.xlsx` (2026-02-22).  10 columns · xlsx.
    // ONE ROW PER ITEM so `내품명 = rack_location` preserves product↔rack 1:1
    // correspondence.  CJ 프로그램의 합포장은 CJ 측 설정으로 자동 처리하므로
    // LIVEMOA 는 정확한 행 단위 데이터만 전달.
    id: 'CJ',
    label: 'CJ대한통운',
    fileExt: 'xlsx',
    encoding: 'utf-8',
    status: 'available',
    headers: ['받는분성명', '받는분전화번호', '받는분기타연락처',
               '받는분주소(전체, 분할)', '품목명', '내품수량', '내품명',
               '배송메세지1', '고객주문번호', '운송장번호'],
    build: (ps) => (ps.items || [{}]).map((it) => {
      // A4-2A-2 · CJ 송장 출력 폭 대응 · 옵션명(key) 은 제거하고 옵션값(value)만 축약.
      //   1개 옵션 → "상품명 값"          (예: 테스트상품 아이보리)
      //   2개+     → "상품명 값1/값2/…"   (예: 롱코트 블랙/L)
      //   0개      → "상품명"
      //   순서    : 옵션 삽입 순서 (typical 색상 → 사이즈).  variant_key 정렬과는
      //             무관 — 이건 사람이 읽을 표시 문자열이지 canonical key 아님.
      //   금지    : (색상=아이보리) 형태 · 수량 · rack · 기타 필드 수정 없음.
      const vals = (it.option && typeof it.option === 'object')
        ? Object.entries(it.option)
            .filter(([, v]) => v !== null && v !== '' && v !== undefined)
            .map(([, v]) => String(v))
        : [];
      const optSuffix = vals.length === 0 ? '' : ` ${vals.join('/')}`;
      return {
        '받는분성명': ps.recipient?.name || '',
        '받는분전화번호': ps.recipient?.phone || '',
        '받는분기타연락처': ps.recipient?.altPhone || '',
        '받는분주소(전체, 분할)': ps.recipient?.address || '',
        '품목명': `${it.name || ''}${optSuffix}`,
        '내품수량': it.quantity || 1,
        // A4-2A · `내품명` ← seller_inventory.rack_location · 미지정이면 빈 값.
        '내품명': it.rackLocation || '',
        '배송메세지1': ps.deliveryMemo || '',
        // Real-verified matching key: CJ 결과 파일의 `주문번호` 컬럼과 정확히 대응.
        '고객주문번호': ps.orderNo || '',
        '운송장번호': '',
      };
    }),
  },

  HANJIN: {
    // Source: 한진택배.csv · UTF-8+BOM · 11 columns · no Alternate Phone
    id: 'HANJIN',
    label: '한진택배',
    fileExt: 'csv',
    encoding: 'utf-8-bom',
    status: 'available',
    headers: ['Sender Customer Name', 'Sender Customer Phone', 'Sender Customer Address',
               'Recipient Customer Name', 'Recipient Customer Phone',
               'Postal Code', 'Base Address',
               'Product Name', 'Quantity', 'Delivery Request', 'Order No.'],
    build: (ps) => [{
      'Sender Customer Name': ps.sender?.name || '',
      'Sender Customer Phone': ps.sender?.phone || '',
      'Sender Customer Address': ps.sender?.address || '',
      'Recipient Customer Name': ps.recipient?.name || '',
      'Recipient Customer Phone': ps.recipient?.phone || '',
      'Postal Code': ps.recipient?.postalCode || '',
      'Base Address': ps.recipient?.address || '',
      'Product Name': _joinItems(ps.items),
      'Quantity': _sumQty(ps.items),
      'Delivery Request': ps.deliveryMemo || '',
      'Order No.': ps.orderNo || ps.combinedShipmentId || '',
    }],
  },

  LOTTE: {
    // Source: 롯데택배.csv · UTF-8+BOM · 11 columns · Customer Order No
    id: 'LOTTE',
    label: '롯데택배',
    fileExt: 'csv',
    encoding: 'utf-8-bom',
    status: 'available',
    headers: ['Sender Name', 'Sender Phone', 'Sender Address',
               'Recipient Name', 'Recipient Phone',
               'Recipient Postal Code', 'Recipient Address',
               'Product Name', 'Quantity', 'Delivery Message', 'Customer Order No.'],
    build: (ps) => [{
      'Sender Name': ps.sender?.name || '',
      'Sender Phone': ps.sender?.phone || '',
      'Sender Address': ps.sender?.address || '',
      'Recipient Name': ps.recipient?.name || '',
      'Recipient Phone': ps.recipient?.phone || '',
      'Recipient Postal Code': ps.recipient?.postalCode || '',
      'Recipient Address': ps.recipient?.address || '',
      'Product Name': _joinItems(ps.items),
      'Quantity': _sumQty(ps.items),
      'Delivery Message': ps.deliveryMemo || '',
      'Customer Order No.': ps.orderNo || ps.combinedShipmentId || '',
    }],
  },

  LOGEN: {
    id: 'LOGEN',
    label: '로젠택배',
    fileExt: null,
    status: 'unregistered',
    headers: [],
    build: () => [],
  },

  KOREA_POST: {
    id: 'KOREA_POST',
    label: '우체국택배',
    fileExt: null,
    status: 'unregistered',
    headers: [],
    build: () => [],
  },
};

/** Serializes headers+rows to a plain CSV string with UTF-8 BOM. */
export const buildCsvBlob = (headers, rows) => {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [];
  lines.push(headers.map(esc).join(','));
  rows.forEach((r) => lines.push(headers.map((h) => esc(r[h])).join(',')));
  const bom = '\uFEFF';
  return new Blob([bom + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
};

/** Given a raw seller_orders list + optional combined docs, build the
 *  canonical PhysicalShipment list (合浦 dedup applied · PHASE 4 reuse). */
export const buildPhysicalShipments = (rows, combinedById, senderInfo = {}) => {
  const shipments = [];
  const emitted = new Set();
  for (const o of rows) {
    const cid = o.combined_shipment_id;
    if (cid && emitted.has(cid)) continue;
    const cdoc = cid ? combinedById[cid] : null;
    const addr = o.shipping_address_snapshot || o.buyer_snapshot || {};
    const recip = cdoc?.recipient_snapshot || addr;
    let items = [];
    if (cdoc) {
      const memberSids = new Set((cdoc.members || []).map((m) => m.seller_order_id));
      const srcMembers = rows.filter((r) => memberSids.has(r.seller_order_id));
      const seen = new Set(srcMembers.map((r) => r.seller_order_id));
      srcMembers.forEach((m) => (m.items || []).forEach((it) => items.push({
        name: it.name_snapshot || it.product_name_snapshot || it.name || it.product_id || '',
        option: it.option || {},
        quantity: it.quantity || 1,
        productId: it.product_id || '',
        rackLocation: it.rack_location || '',   // A4-2A · injected by caller via rackMap
      })));
      (cdoc.members || []).forEach((m) => {
        if (seen.has(m.seller_order_id)) return;
        (m.items_included || []).forEach((it) => items.push({
          name: it.product_id || '', option: {}, quantity: it.quantity || 1,
          productId: it.product_id || '', rackLocation: '',
        }));
      });
      emitted.add(cid);
    } else {
      items = (o.items || [{}]).map((it) => ({
        name: it.name_snapshot || it.product_name_snapshot || it.name || it.product_id || '',
        option: it.option || {},
        quantity: it.quantity || 1,
        productId: it.product_id || '',
        rackLocation: it.rack_location || '',   // A4-2A
      }));
    }
    shipments.push({
      orderNo: cdoc ? (o.order_number || cdoc.members?.[0]?.seller_order_id || cid) : (o.order_number || ''),
      sellerOrderId: o.seller_order_id,
      combinedShipmentId: cid || null,
      recipient: {
        name: recip.recipient_name || recip.name || o.buyer_name || '',
        phone: recip.phone || o.buyer_snapshot?.phone || '',
        altPhone: recip.alt_phone || recip.phone2 || '',
        postalCode: recip.postal_code || '',
        address: [recip.address1, recip.address2].filter(Boolean).join(' '),
      },
      items,
      deliveryMemo: o.delivery_message || addr.delivery_message || '',
      sender: senderInfo,
    });
  }
  return shipments;
};
